import type { PagedResultDto } from "@services/dto/pagedResultDto"
import http from "../httpService"
import { L, LNotification } from "@lib/abpUtility"
import { notifyError, notifySuccess } from "@lib/helper"
import {
  RowUnitModel,
  UnitModel,
  UnitUpdateMasterModel,
} from "@models/project/unitModel"
import { downloadFile } from "@lib/helperFile"
import { ignoreBodyByModel } from "@models/global"
import dayjs from "dayjs"

const getUnitAuditFieldLabels = (): Record<string, string> => ({
  UnitName:                 L("UNIT_DETAIL_UNIT_NAME"),
  FullUnitCode:             L("UNIT_DETAIL_UNIT_CODE"),
  Price:                    L("UNIT_DETAIL_PRICE"),
  StatusId:                 L("UNIT_DETAIL_UNIT_STATUS"),
  ProductTypeId:            L("UNIT_DETAIL_PRODUCT_TYPE"),
  UnitTypeId:               L("UNIT_DETAIL_UNIT_TYPE"),
  BedRoom:                  L("UNIT_DETAIL_BEDROOM"),
  BathRoom:                 L("UNIT_DETAIL_BATHROOM"),
  UsableSize:               L("UNIT_DETAIL_NET_AREA"),
  ConstructionSize:         L("UNIT_DETAIL_CONSTRUCTION_AREA"),
  GrossFloorArea:           L("UNIT_DETAIL_FLOOR_AREA"),
  TotalFloorArea:           L("UNIT_DETAIL_FLOOR_AREA"),
  ViewId:                   L("UNIT_DETAIL_VIEW"),
  KitchenViewId:            L("UNIT_DETAIL_KITCHEN_VIEW"),
  FloorId:                  L("UNIT_DETAIL_FLOOR"),
  IsActive:                 L("LOAN_PROGRAM_DETAIL_IS_ACTIVE"),
  OwnerShipStatus:          L("UNIT_DETAIL_OWNER_SHIP_STATUS"),
  LegalStatus:              L("UNIT_DETAIL_LEGAL_STATUS"),
  VatPercentage:            L("CONTRACT_BOOKING_THUE_VAT_5"),
  IsPricingApproved:        L("UNIT_DETAIL_ELIGIBLE_FOR_PINKBOOK"),
  HandoverUnitStatusId:     L("STATUS_HANDOVER_APARTMENT"),
  GFAPricePerSquareMeter:   L("UNIT_DETAIL_PRICE"),
  NFAPricePerSquareMeter:   L("UNIT_DETAIL_PRICE"),
})

class UnitService {
  public async create(body: UnitUpdateMasterModel) {
    const filteredBody = ignoreBodyByModel(body, UnitUpdateMasterModel)

    const result = await http.post(
      "api/services/app/Unit/CreateMasterData",
      filteredBody
    )
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async update(body: UnitUpdateMasterModel) {
    //body ignore other model

    const filteredBody = ignoreBodyByModel(body, UnitUpdateMasterModel)

    const result = await http.put(
      `api/services/app/Unit/UpdateMasterData`,
      filteredBody
    )
    // notifySuccess(
    //   LNotification("SUCCESS"),
    //   LNotification("SAVING_SUCCESSFULLY")
    // )
    return result.data.result
  }

  public async updateDetail(body: any) {
    // const filteredBody = ignoreBodyByModel(body, UnitUpdateModel)

    const result = await http.put(`api/services/app/Unit/UpdateDetail`, body)
    notifySuccess(
      LNotification("SUCCESS"),
      LNotification("SAVING_SUCCESSFULLY")
    )
    return result.data.result
  }

  public async delete(id: number) {
    const result = await http.delete("api/services/app/Residents/Delete", {
      params: { id },
    })
    return result.data
  }

  public async activateOrDeactivate(id: number, isActive) {
    const result = await http.post(
      "api/services/app/Unit/Active",
      { id },
      { params: { isActive } }
    )
    return result.data
  }

  public async get(id: number): Promise<any> {
    if (!id) {
      notifyError(L("Error"), L("EntityNotFound"))
    }

    const res = await http.get(`api/services/app/Unit/Get`, {
      params: { id },
    })

    const { result } = res.data

    return UnitModel.assign(result)
  }

  public async getAll(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Unit/GetAll", {
      params,
    })
    const { result } = res.data
    result.items = RowUnitModel.assigns(result.items)

    return result
  }
  public async GetAllForCart(params: any): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Unit/GetAllForCart", {
      params,
    })
    const { result } = res.data
    result.items = RowUnitModel.assigns(result.items)

    return result
  }

  public async getAllUnitMatchingInquiry(
    params: any
  ): Promise<PagedResultDto<any>> {
    const res = await http.get("api/services/app/Unit/GetMatchingUnit", {
      params,
    })
    const { result } = res.data
    result.items = RowUnitModel.assigns(result.items)

    return result
  }

  public async getUnits(params) {
    if (!params.skipCount) {
      params.skipCount = 0
    }
    if (!params.maxResultCount) {
      params.maxResultCount = 20
    }
    const response = await http.get("api/services/app/Units/GetUnits", {
      params,
    })
    return response.data.result.items
  }
  public async getListReadyForHandover(params) {
    const response = await http.get(
      "api/services/app/Unit/GetListReadyForHandover",
      {
        params,
      }
    )
    return response.data.result
  }
  public async getListFloorByTower(towerId): Promise<any> {
    const res = await http.get("api/services/app/Unit/GetListFloorByTower", {
      params: { towerId },
    })
    const { result } = res.data
    return result
  }
  public async getList(params: any): Promise<any> {
    const res = await http.get("api/services/app/Unit/GetList", {
      params,
    })
    const { result } = res.data

    return result
  }

  public async getListForContract(params: any): Promise<any> {
    const res = await http.get("api/services/app/Unit/GetListForContract", {
      params,
    })
    const { result } = res.data

    return result
  }
  public async getListForPriceBook(params: any): Promise<any> {
    const res = await http.get("api/services/app/Unit/GetListForPriceBook", {
      params,
    })
    const { result } = res.data

    return result
  }
  public async exportExcel(params: any): Promise<any> {
    const res = await http.get("api/Export/ExportUnit", {
      params,
      responseType: "blob",
    })
    downloadFile(res.data, `EXPORT-UNIT-${dayjs().format("DDMMYYYYHHmmss")}.xlsx`)
  }

  public async getListUnitPricingHistory(params: any) {
    const res = await http.get(
      "api/services/app/PricingApproval/GetListUnitPricingHistory",
      {
        params,
      }
    )
    return res.data.result
  }
  public async getAuditLog(id: any) {
    const res = await http.get("api/services/app/Unit/GetAuditLogs", {
      params: { id },
    })
    const labels = getUnitAuditFieldLabels()
    return (res.data.result || []).map((row: any) => {
      ;(row.items || []).forEach((item: any) => {
        item.propertyName = labels[item.propertyName] ?? item.propertyName
      })
      return row
    })
  }

  public async getListUnitForBooking(params: any): Promise<any> {
    const res = await http.get("api/services/app/Unit/GetListForBooking", {
      params,
    })
    const result = res.data.result

    return result
  }
}

export default new UnitService()
